import React from 'react';
import { Formik, Field, Form } from 'formik';
import PropTypes from 'prop-types';
import { Location } from '@gatsbyjs/reach-router';
import { getAnalyticsParams } from '../../utils/session-storage';
import { defaultDonationCampaignId } from '../../variables';
import { sendEvent } from '../../utils/analytics';
import { site } from '../../utils/current-site';

import HiddenAmountField from './hidden-amount-field';
/**
 * The donation form handler is a form that includes several fields
 * required for processing a donation, including appeal codes, and
 * a hidden amount field that passes the penny-formatted value to the
 * donation script
 */
const DonationFormHandler = ({
  defaultDonationValue,
  campaignId,
  className,
  render,
  frequency,
  inline,
  id,
  dataTracking,
}) => {
  const defaultValue = defaultDonationValue;
  const defaultPennyValue = defaultValue * 100;

  const { utmParams, gclid, reservedAppealCode } = getAnalyticsParams();

  return (
    <Location>
      {() => (
        <Formik
          onSubmit={(values) => {
            sendEvent({
              donationValue: values.amount / 100,
              donationFrequency: values.frequency,
              interactType: 'Donation Submit',
              event: 'donationSubmit',
              ...dataTracking,
            });
          }}
          initialValues={{
            cid: campaignId || defaultDonationCampaignId[site],
            free_amount: '1',
            'amount-holder': '',
            amount: defaultPennyValue.toString(),
            reserved_appeal_code: reservedAppealCode || '',
            frequency,
          }}
        >
          {({ submitForm, setFieldValue, values }) => (
            <Form
              style={{ display: inline ? 'flex' : '' }}
              action="https://donate.shelter.org.uk/b"
              method="GET"
              className={className}
              onSubmit={submitForm}
              id={id || null}
              name="donation"
            >
              <HiddenAmountField
                name="amount"
                defaultPennyValue={defaultPennyValue}
              />
              <Field type="hidden" name="cid" />
              <Field type="hidden" name="free_amount" />
              <Field type="hidden" name="reserved_appeal_code" />
              <Field type="hidden" name="frequency" />
              {utmParams
                ? Object.entries(utmParams).map((utm) => (
                    <Field
                      type="hidden"
                      name={`reserved_${utm[0]}`}
                      value={utm[1]}
                    />
                  ))
                : null}

              {gclid ? (
                <Field type="hidden" name="reserved_gclid" value={gclid} />
              ) : null}
              {render({ setFieldValue, defaultValue, values })}
            </Form>
          )}
        </Formik>
      )}
    </Location>
  );
};

DonationFormHandler.propTypes = {
  className: PropTypes.string,
  defaultDonationValue: PropTypes.number,
  campaignId: PropTypes.number,
  render: PropTypes.func.isRequired,
  frequency: PropTypes.oneOf(['once', 'regular']),
  inline: PropTypes.bool,
  id: PropTypes.string,
  dataTracking: PropTypes.shape({
    donationDescription: PropTypes.string,
    donationImage: PropTypes.string,
  }),
};

DonationFormHandler.defaultProps = {
  frequency: 'once',
  inline: true,
  defaultDonationValue: 30,
  campaignId: defaultDonationCampaignId[site],
};

export default DonationFormHandler;
